import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Mail, Phone, ArrowRight, FileText, Database, Settings, Share2, Clock, Lock, UserX, Ban, Scale, RefreshCw, MessageSquare, Edit3 } from 'lucide-react';
const sections = [{
  num: '1',
  icon: Database,
  title: 'What Information Do We Collect?',
  body: ['We collect personal information that you voluntarily provide: Names, Email addresses, and Phone numbers. We also automatically collect technical information such as IP address, browser type, device data, and location data when you visit our Services.'],
  callout: {
    label: 'Sensitive Information',
    text: 'We do not process sensitive information.'
  }
}, {
  num: '2',
  icon: Settings,
  title: 'How Do We Process Your Information?',
  body: ['We process your information to provide, improve, and administer our Services, communicate with you, for security and fraud prevention, and to comply with law.']
}, {
  num: '3',
  icon: Share2,
  title: 'When and With Whom Do We Share Your Personal Information?',
  bullets: [{
    label: 'Business Transfers:',
    text: 'In connection with any merger, sale of company assets, or acquisition.'
  }, {
    label: 'Business Partners:',
    text: 'To offer you certain products, services, or promotions.'
  }]
}, {
  num: '4',
  icon: Clock,
  title: 'How Long Do We Keep Your Information?',
  body: ['We keep your personal information only as long as necessary for the purposes outlined in this privacy notice. When no longer needed, we will delete or anonymize such information.']
}, {
  num: '5',
  icon: Lock,
  title: 'How Do We Keep Your Information Safe?',
  body: ['We have implemented appropriate technical and organizational security measures. However, no electronic transmission over the internet can be guaranteed to be 100% secure.']
}, {
  num: '6',
  icon: UserX,
  title: 'Do We Collect Information From Minors?',
  body: [<>
        We do not knowingly collect data from children under 18 years of age. By
        using our Services, you represent that you are at least 18. Contact us
        at{' '}
        <a href="mailto:info@ics-claims.com" className="text-brand-navy font-semibold underline decoration-brand-gold/50 underline-offset-2 hover:text-brand-gold transition-colors" data-id="element-860">
          info@ics-claims.com
        </a>{' '}
        if you believe we have collected data from a minor.
      </>]
}, {
  num: '7',
  icon: Ban,
  title: 'Controls For Do-Not-Track Features',
  body: ['We do not currently respond to DNT browser signals. California law requires us to inform you that because there is no industry or legal standard for recognizing or honoring DNT signals, we do not respond to them at this time.']
}, {
  num: '8',
  icon: Scale,
  title: 'Do United States Residents Have Specific Privacy Rights?',
  body: ["California residents may have the right to request access to, correct, or delete their personal information. We have not disclosed, sold, or shared any personal information to third parties for a business or commercial purpose in the preceding twelve (12) months outside of service providers, payors, or vendors of an active workers' compensation claim."],
  bullets: [{
    label: 'Your Rights:',
    text: 'Right to access, correct, delete, and obtain a copy of your data; right to non-discrimination; right to opt out of targeted advertising.'
  }, {
    label: 'To Exercise Your Rights:',
    textNode: <>
            Visit{' '}
            <Link to="/contact" className="text-brand-navy font-semibold underline decoration-brand-gold/50 underline-offset-2 hover:text-brand-gold transition-colors" data-id="element-861">
              arissacs.com/contact
            </Link>
            , email{' '}
            <a href="mailto:info@ArissaCS.com" className="text-brand-navy font-semibold underline decoration-brand-gold/50 underline-offset-2 hover:text-brand-gold transition-colors" data-id="element-862">
              info@ArissaCS.com
            </a>
            , or call{' '}
            <a href="tel:8552601053" className="text-brand-navy font-semibold underline decoration-brand-gold/50 underline-offset-2 hover:text-brand-gold transition-colors" data-id="element-863">
              855-260-1053
            </a>
            .
          </>
  }]
}, {
  num: '9',
  icon: RefreshCw,
  title: 'Do We Make Updates to This Notice?',
  body: ['Yes, we will update this notice as necessary to stay compliant with relevant laws. The updated version will be indicated by an updated "Last Updated" date at the top of this notice, and the updated version will be effective as soon as it is accessible.']
}, {
  num: '10',
  icon: MessageSquare,
  title: 'How Can You Contact Us About This Notice?',
  body: [<>
        If you have questions or comments about this notice, you may email us at{' '}
        <a href="mailto:info@ArissaCS.com" className="text-brand-navy font-semibold underline decoration-brand-gold/50 underline-offset-2 hover:text-brand-gold transition-colors" data-id="element-864">
          info@ArissaCS.com
        </a>{' '}
        or call us at{' '}
        <a href="tel:8552601053" className="text-brand-navy font-semibold underline decoration-brand-gold/50 underline-offset-2 hover:text-brand-gold transition-colors" data-id="element-865">
          855-260-1053
        </a>
        .
      </>]
}, {
  num: '11',
  icon: Edit3,
  title: 'How Can You Review, Update, or Delete the Data We Collect From You?',
  body: [<>
        Based on the applicable laws of your country or state of residence in
        the U.S., you may have the right to request access to the personal
        information we collect from you, details about how we have processed it,
        correct inaccuracies, or delete your personal information. To request to
        review, update, or delete your personal information, please{' '}
        <Link to="/contact" className="text-brand-navy font-semibold underline decoration-brand-gold/50 underline-offset-2 hover:text-brand-gold transition-colors" data-id="element-866">
          contact us
        </Link>
        .
      </>]
}];
export function Privacy() {
  return <div className="bg-brand-paper" data-id="element-867">
      {/* Hero */}
      <section className="relative pt-40 pb-24 overflow-hidden" data-id="element-868">
        <div className="absolute inset-0 bg-cover bg-center" style={{
        backgroundImage: 'url(https://images.unsplash.com/photo-1521737711867-e3b97375f902?ixlib=rb-4.0.3&auto=format&fit=crop&w=2400&q=90)'
      }} data-id="element-869"></div>
        <div className="absolute inset-0 bg-brand-navy/92 mix-blend-multiply" data-id="element-870"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-brand-navy via-brand-navy/85 to-brand-navy/60" data-id="element-871"></div>
        <div className="absolute inset-0 opacity-10" data-id="element-872">
          <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(#C9A24B 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }} data-id="element-873"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-874">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-3xl" data-id="element-875">
            <div className="inline-flex items-center space-x-2 bg-white/5 border border-white/10 rounded-full px-4 py-1.5 mb-6" data-id="element-876">
              <Shield className="w-3.5 h-3.5 text-brand-gold" data-id="element-877" />
              <span className="text-xs font-bold tracking-widest text-brand-gold uppercase" data-id="element-878">
                Privacy Notice
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-medium text-white leading-[1.1] mb-6 text-balance" data-id="element-879">
              Your <span className="text-brand-gold italic" data-id="element-880">privacy</span>{' '}
              matters.
            </h1>
            <p className="text-xl text-white/85 leading-relaxed font-light max-w-2xl" data-id="element-881">
              This privacy notice for Arissa Cost Solutions, Inc. describes how
              and why we might collect, store, use, and/or share your
              information when you use our services, such as when you visit our
              website at arissacs.com or engage with us in other related ways.
            </p>
            <p className="mt-6 text-sm text-brand-gold font-semibold tracking-widest uppercase" data-id="element-882">
              Last Updated: June 05, 2024
            </p>
          </motion.div>
        </div>
      </section>

      {/* Quick contact callout */}
      <section className="bg-white -mt-12 relative z-10" data-id="element-883">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-884">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 md:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5" data-id="element-885">
            <div className="w-14 h-14 rounded-xl bg-brand-gold/10 text-brand-gold flex items-center justify-center flex-shrink-0" data-id="element-886">
              <FileText className="w-7 h-7" data-id="element-887" />
            </div>
            <div className="flex-1" data-id="element-888">
              <h3 className="font-serif font-bold text-xl text-brand-navy mb-1" data-id="element-889">
                Questions or concerns?
              </h3>
              <p className="text-gray-600 text-sm" data-id="element-890">
                Contact us at{' '}
                <a href="mailto:info@ArissaCS.com" className="text-brand-navy font-semibold underline decoration-brand-gold/50 underline-offset-2 hover:text-brand-gold transition-colors" data-id="element-891">
                  info@ArissaCS.com
                </a>
                .
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Policy Sections */}
      <section className="py-20 bg-brand-paper" data-id="element-892">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6" data-id="element-893">
          {sections.map((section, i) => {
          const Icon = section.icon;
          return <motion.div key={section.num} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-80px'
          }} transition={{
            duration: 0.5,
            delay: Math.min(i * 0.03, 0.2)
          }} className="bg-white rounded-2xl p-8 md:p-10 shadow-sm border border-gray-100" data-id="element-894">
                <div className="flex items-start gap-5 mb-6" data-id="element-895">
                  <div className="hidden sm:flex w-12 h-12 rounded-lg bg-brand-navy text-brand-gold items-center justify-center flex-shrink-0" data-id="element-896">
                    <Icon className="w-6 h-6" data-id="element-897" />
                  </div>
                  <div className="flex-1" data-id="element-898">
                    <div className="text-brand-gold font-bold tracking-widest text-xs uppercase mb-2" data-id="element-899">
                      Section {section.num}
                    </div>
                    <h3 className="font-serif font-bold text-2xl md:text-3xl text-brand-navy leading-tight" data-id="element-900">
                      {section.title}
                    </h3>
                  </div>
                </div>

                <div className="space-y-4 sm:pl-[68px]" data-id="element-901">
                  {section.body?.map((p, idx) => <p key={idx} className="text-gray-700 leading-relaxed" data-id="element-902">
                      {p}
                    </p>)}

                  {section.callout && <div className="bg-brand-paper border-l-4 border-brand-gold rounded-r-lg p-4" data-id="element-903">
                      <span className="font-bold text-brand-navy" data-id="element-904">
                        {section.callout.label}:
                      </span>{' '}
                      <span className="text-gray-700" data-id="element-905">
                        {section.callout.text}
                      </span>
                    </div>}

                  {section.bullets && <ul className="space-y-3 pt-2" data-id="element-906">
                      {section.bullets.map((b: any, idx: number) => <li key={idx} className="flex items-start" data-id="element-907">
                          <span className="w-1.5 h-1.5 rounded-full bg-brand-gold mt-2.5 mr-4 flex-shrink-0" data-id="element-908"></span>
                          <span className="text-gray-700 leading-relaxed" data-id="element-909">
                            <span className="font-bold text-brand-navy" data-id="element-910">
                              {b.label}
                            </span>{' '}
                            {b.textNode || b.text}
                          </span>
                        </li>)}
                    </ul>}
                </div>
              </motion.div>;
        })}
        </div>
      </section>

      {/* Contact callout */}
      <section className="py-20 bg-white" data-id="element-911">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-912">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="bg-brand-navy rounded-2xl p-10 md:p-12 text-white relative overflow-hidden" data-id="element-913">
            <div className="absolute -top-10 -right-10 w-64 h-64 bg-brand-gold/15 rounded-full blur-3xl" data-id="element-914"></div>
            <div className="absolute top-0 left-0 w-full h-1 bg-brand-gold" data-id="element-915"></div>

            <div className="relative z-10 grid md:grid-cols-2 gap-10 items-center" data-id="element-916">
              <div data-id="element-917">
                <h3 className="font-serif text-3xl md:text-4xl text-white mb-4 leading-tight" data-id="element-918">
                  Need to exercise your rights?
                </h3>
                <p className="text-white/80 leading-relaxed" data-id="element-919">
                  Reach out anytime to access, update, or delete your
                  information — or to ask any privacy-related question.
                </p>
              </div>
              <div className="space-y-4" data-id="element-920">
                <a href="mailto:info@ArissaCS.com" className="flex items-center bg-white/10 backdrop-blur-sm border border-white/15 rounded-xl px-5 py-4 hover:bg-white/15 transition-colors" data-id="element-921">
                  <Mail className="w-5 h-5 text-brand-gold mr-4 flex-shrink-0" data-id="element-922" />
                  <div data-id="element-923">
                    <div className="text-xs uppercase tracking-wider text-white/60 font-semibold" data-id="element-924">
                      Email
                    </div>
                    <div className="text-white" data-id="element-925">info@ArissaCS.com</div>
                  </div>
                </a>
                <a href="tel:8552601053" className="flex items-center bg-white/10 backdrop-blur-sm border border-white/15 rounded-xl px-5 py-4 hover:bg-white/15 transition-colors" data-id="element-926">
                  <Phone className="w-5 h-5 text-brand-gold mr-4 flex-shrink-0" data-id="element-927" />
                  <div data-id="element-928">
                    <div className="text-xs uppercase tracking-wider text-white/60 font-semibold" data-id="element-929">
                      Phone
                    </div>
                    <div className="text-white" data-id="element-930">855-260-1053</div>
                  </div>
                </a>
                <Link to="/contact" className="flex items-center justify-between bg-brand-gold text-brand-navy rounded-xl px-5 py-4 hover:bg-white transition-colors font-semibold" data-id="element-931">
                  Contact our team
                  <ArrowRight className="w-5 h-5" data-id="element-932" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}